import { formatLocale } from 'd3-format';
import { DateTime } from 'luxon';

import { X_AXIS_DOMAIN_DYNAMIC, X_AXIS_DOMAIN_FIXED } from 'src/util/constants';
import { getLocaleNumberFormat } from 'src/util/i18n/constants';
import { toMillis } from 'src/util/time';

import {
  BAR_RATIO, BUY, DURATION_P1D_MS, DURATION_PT30M_MS, FALLBACK_BAR_WIDTH, SELL,
  UNIT_CURRENCY_AXIS_FORMAT_SMALL_VALUE, Y_AXIS_DOMAIN_MULTIPLIER, chartPadding,
} from '../components/chartConstants';

/**
 * Comparator function for sorting chart data points by their x-coordinate (timestamp).
 * Used to ensure data points are rendered in chronological order in the chart.
 * @param {{ x: number }} a - First data point with an x property (typically a timestamp in ms).
 * @param {{ x: number }} b - Second data point with an x property (typically a timestamp in ms).
 * @returns {number} Negative if a comes before b, positive if a comes after b, zero if equal.
 * @example
 * const dataPoints = [{ x: 1000, y: 5 }, { x: 500, y: 3 }, { x: 750, y: 4 }];
 * dataPoints.sort(compareByX);
 * // Result: [{ x: 500, y: 3 }, { x: 750, y: 4 }, { x: 1000, y: 5 }]
 */
export const compareByX = (a, b) => a.x - b.x;

/**
 * Because this is a stacked set of values we need to aggregate by date then
 * determine the maximum / minimum.
 * @param {object} rawChartData
 * @returns {object} domain updated based on the raw chart data.
 */
export const processDomainByDataset = (rawChartData) => {
  const updatedDomain = { x: null, y: null };

  if (!rawChartData) {
    return updatedDomain;
  }
  const yValueAggregates = {};

  [SELL, BUY].forEach((dir) => {
    const datasets = rawChartData[dir];
    const yMultiplier = dir === SELL ? -1 : 1;

    yValueAggregates[dir] ||= {};

    Object.keys(datasets).forEach((id) => {
      const dataset = datasets[id];
      Object.keys(dataset).forEach((timestamp) => {
        const datum = dataset[timestamp] || {};
        if (datum) {
          updatedDomain.x ||= [datum.x, datum.x];

          if (updatedDomain.x[0] > datum.x) {
            updatedDomain.x[0] = datum.x;
          } else if (updatedDomain.x[1] < datum.x) {
            updatedDomain.x[1] = datum.x;
          }

          const yValue = yMultiplier * datum.y;

          yValueAggregates[dir][timestamp] ||= 0;
          yValueAggregates[dir][timestamp] += yValue;
        }
      });
    });
  });

  const yMin = Math.min(...Object.values(yValueAggregates[SELL]), 0);
  const yMax = Math.max(...Object.values(yValueAggregates[BUY]), 0);

  updatedDomain.y = [yMin, yMax];

  return updatedDomain;
};

/**
 * Processes the domain based on the timespan.
 * @param {object} rawChartData
 * @param {object} timespan
 * @param {string} stepSize - P1D or PT30M
 * @returns {object} domain object.
 */
export const processDomainByTimespan = (rawChartData, timespan, stepSize) => {
  if (!rawChartData || !timespan || !stepSize) {
    return { x: null, y: null };
  }
  const updatedDomain = { x: [timespan.start, timespan.finish], y: null };
  const yValueAggregates = {};

  [SELL, BUY].forEach((dir) => {
    const datasets = rawChartData[dir];
    const yMultiplier = dir === SELL ? -1 : 1;

    yValueAggregates[dir] ||= {};

    Object.keys(datasets).forEach((id) => {
      const dataset = datasets[id];
      Object.keys(dataset).forEach((timestamp) => {
        const datum = dataset[timestamp] || {};
        if (datum) {
          const yValue = yMultiplier * datum.y;

          yValueAggregates[dir][timestamp] ||= 0;
          yValueAggregates[dir][timestamp] += yValue;
        }
      });
    });
  });

  switch (stepSize) {
    case 'P1D':
      updatedDomain.x[1] = updatedDomain.x[1].minus({ day: 1 });
      break;
    case 'PT30M':
      updatedDomain.x[0] = updatedDomain.x[0].plus({ minutes: 30 });
      break;
    default:
      throw new Error(`Error: '${stepSize}' invalid`);
  }

  const yMin = Math.min(...Object.values(yValueAggregates[SELL]), 0);
  const yMax = Math.max(...Object.values(yValueAggregates[BUY]), 0);

  updatedDomain.y = [yMin, yMax];

  return updatedDomain;
};

/**
 * @param {object} rawChartData
 * @param {string} stepSize
 * @param {object} timespan
 * @param {X_AXIS_DOMAIN_FIXED | X_AXIS_DOMAIN_DYNAMIC} xAxisDomain
 * @returns {object} domain object.
 */
export const chartDomain = (rawChartData, stepSize, timespan, xAxisDomain) => {
  const xAxisDomainFixed = xAxisDomain === X_AXIS_DOMAIN_FIXED;
  let xAxisDomainPadding = 0;
  switch (stepSize) {
    case 'P1D':
      xAxisDomainPadding = 24 * 60 * 60 * 1000; // 24 hrs in ms
      break;
    case 'PT30M':
      xAxisDomainPadding = 30 * 60 * 1000; // 30 mins in ms
      break;
    default:
      throw new Error(`Error: '${stepSize}' invalid`);
  }
  // Set the domain based on the user selection.
  const domain = xAxisDomainFixed
    ? processDomainByTimespan(rawChartData, timespan, stepSize)
    : processDomainByDataset(rawChartData);

  // Set the defaults if needed.
  if (domain.x === null || domain.y === null) {
    return { x: [0, 1], y: [0, 1] };
  }

  if (domain.y[0] > 0) { domain.y[0] = 0; }
  if (domain.y[1] < 0) { domain.y[1] = 0; }

  // Pad it out based on the bar chart step size.
  domain.x[0] -= xAxisDomainPadding / 2;
  domain.x[1] += xAxisDomainPadding / 2;
  // Pad out the y-values where they're above/below.
  if (domain.y[0] < 0) { domain.y[0] *= Y_AXIS_DOMAIN_MULTIPLIER; }
  if (domain.y[1] > 0) { domain.y[1] *= Y_AXIS_DOMAIN_MULTIPLIER; }

  return domain;
};

const formatMillisecond = 'SSS';
const formatSecond = ':ss';
const formatMinute = 'HH:mm';
const formatHour = 'h a';
const formatDay = 'ccc dd';
const formatWeek = 'LLL dd';
const formatMonth = 'LLLL';
const formatYear = 'yyyy';

/**
 * Formats the ticks that get rendered in the axis of the bar chart.
 * @param {string} zone - The time zone to use for formatting the ticks.
 * @returns {Function} - A function that formats the ticks in the bar chart axis.
 *   The returned function takes a tick as input and returns a formatted string.
 */
// eslint-disable-next-line arrow-body-style
export const handleTickFormat = (zone) => {
  /**
   * Formats a single tick value.
   * @param {string} tick - The tick to be formatted, provided as a JavaScript Date string.
   * @returns {string} - The formatted tick value.
   */
  return (tick) => {
    const timestamp = DateTime.fromMillis(tick.getTime()).setZone(zone);

    if (timestamp.startOf('second') < timestamp) {
      return timestamp.toFormat(formatMillisecond);
    }

    if (timestamp.startOf('minute') < timestamp) {
      return timestamp.toFormat(formatSecond);
    }

    if (timestamp.startOf('hour') < timestamp) {
      return timestamp.toFormat(formatMinute);
    }

    if (timestamp.startOf('day') < timestamp) {
      return timestamp.toFormat(formatHour);
    }

    if (timestamp.startOf('month') < timestamp) {
      if (timestamp.startOf('week') < timestamp) {
        return timestamp.toFormat(formatDay);
      }
      return timestamp.toFormat(formatWeek);
    }

    if (timestamp.startOf('year') < timestamp) {
      return timestamp.toFormat(formatMonth);
    }

    return timestamp.toFormat(formatYear);
  };
};

/**
 * Compute a fixed bar width in pixels based on step size and chart dimensions.
 * @param {number} width - Total chart width in pixels.
 * @param {('P1D'|'PT30M')} stepSize - The aggregation step size (daily or 30-minute).
 * @param {{ x: [number, number] }} domain - Domain object with x-range in epoch ms.
 * @returns {number} barWidth - The calculated bar width in pixels.
 */
export const buildBarWidth = (width, stepSize, domain) => {
  // Validate and derive step range
  let stepRange = null;
  switch (stepSize) {
    case 'P1D':
      stepRange = DURATION_P1D_MS;
      break;
    case 'PT30M':
      stepRange = DURATION_PT30M_MS;
      break;
    default:
      return FALLBACK_BAR_WIDTH; // safe fallback for unsupported step sizes
  }

  const leftPad = Number(chartPadding?.left) || 0;
  const rightPad = Number(chartPadding?.right) || 0;
  const numericWidth = Number(width) || 0;

  const innerWidth = Math.max(0, numericWidth - leftPad - rightPad);
  if (innerWidth === 0) return 1;

  const x0 = toMillis(domain?.x?.[0]);
  const x1 = toMillis(domain?.x?.[1]);
  if (x0 == null || x1 == null) return FALLBACK_BAR_WIDTH;

  const totalRangeRaw = x1 - x0;
  const totalRange = Number.isFinite(totalRangeRaw) ? Math.max(0, totalRangeRaw) : 0;

  const stepCount = Math.max(1, Math.ceil(totalRange / stepRange));

  const widthPerStep = innerWidth / stepCount;
  return Math.max(1, Math.floor(widthPerStep * BAR_RATIO));
};

/**
 * Creates a currency formatter that avoids SI prefix notation for values less than $1.
 *
 * The standard d3-format SI prefix notation (`$.5~s`) displays sub-dollar values using
 * the milli prefix (e.g., `$500m` for $0.50), which can be confusing for financial data.
 * This function creates a formatter that uses fixed-point notation for values < $1
 * while preserving SI prefix notation for larger values.
 *
 * This function includes a locale-aware fallback mechanism: if the primary localeObj
 * is null or undefined, it will attempt to create a locale-specific formatter using
 * the provided locale string and currency symbol, ensuring that decimal separators,
 * thousands separators, currency symbols, and other locale-specific formatting rules
 * are respected even in fallback scenarios.
 * @param {object|null} localeObj - The d3 locale object from formatDefaultLocale.
 * If provided, uses locale-specific formatting (decimal separator, thousands separator,
 * currency symbol). If null, falls back to creating a locale formatter using the locale
 * and currencySymbol parameters.
 * @param {string} siFormat - The SI prefix format string for values >= $1 (e.g., '$.5~s').
 * @param {string} [locale='en-AU'] - The locale string (e.g., 'it', 'en-AU', 'fr-FR') used for
 *   fallback formatting when localeObj is null. Determines decimal separator, thousands separator,
 *   and grouping patterns. Defaults to 'en-AU' if not provided.
 * @param {string} [currencySymbol='$'] - The currency symbol (e.g., '$', '€', '£', '¥') used for
 *   fallback formatting when localeObj is null. Should match the user's locale currency.
 *   Defaults to '$' if not provided.
 * @returns {Function} A formatter function that takes a numeric value and returns
 *   a formatted currency string.
 * @example
 * // With locale object (e.g., Italian: decimal=',', thousands='.', currency='€')
 * const localeObj = formatDefaultLocale({ decimal: ',', thousands: '.',
 * currency: ['€', ''], grouping: [3] });
 * const formatter = buildCurrencyAxisFormatter(localeObj, '$.5~s', 'it', '€');
 * formatter(0.50);    // '€0,5' (fixed-point, not '€500m')
 * formatter(1500);    // '€1,5k' (SI prefix)
 * formatter(1000000); // '€1M'   (SI prefix)
 * @example
 * // Without locale object but with locale string and currency symbol (fallback)
 * const formatter = buildCurrencyAxisFormatter(null, '$.5~s', 'it', '€');
 * formatter(0.05);    // '€0,05' (uses Italian decimal separator and Euro symbol)
 * formatter(2500000); // '€2,5M' (uses Italian decimal separator and Euro symbol)
 * @example
 * // Without locale object, defaults to en-AU with $ symbol
 * const formatter = buildCurrencyAxisFormatter(null, '$.5~s');
 * formatter(0.05);    // '$0.05' (uses en-AU decimal separator and $ symbol)
 * formatter(2500000); // '$2.5M' (uses en-AU decimal separator and $ symbol)
 */
export const buildCurrencyAxisFormatter = (
  localeObj,
  siFormat,
  locale = 'en-AU',
  currencySymbol = '$',
) => {
  // Create a locale-aware fallback formatter if localeObj is null
  let formatFn;
  if (localeObj) {
    formatFn = localeObj.format.bind(localeObj);
  } else {
    // Fallback: create a locale-specific formatter using the locale string and currency symbol
    const numberFormat = getLocaleNumberFormat(locale);
    const fallbackLocaleObj = formatLocale({
      decimal: numberFormat.decimal,
      thousands: numberFormat.thousands,
      grouping: numberFormat.grouping,
      currency: [currencySymbol, ''],
    });
    formatFn = fallbackLocaleObj.format.bind(fallbackLocaleObj);
  }

  const siFormatter = formatFn(siFormat);
  const fixedFormatter = formatFn(UNIT_CURRENCY_AXIS_FORMAT_SMALL_VALUE);

  return (value) => {
    // Use fixed-point notation for non-zero values less than $1
    // to avoid confusing milli prefix (e.g., $500m instead of $0.50)
    if (Math.abs(value) < 1 && value !== 0) {
      return fixedFormatter(value);
    }
    return siFormatter(value);
  };
};
